import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import {IecTask} from '../iec-task';
import {TaskResponse} from '../task-response';
import {Entity} from '../../entity/entity';
import {PersonLogin, RoleName} from '../../person/person-login';
import {IecTaskService} from '../../services/iec-task-service';
import { Router } from '@angular/router';
import {PersonLoginService} from '../../services/person-login-service';
import {TaskResponseService} from '../../services/task-response-service';
import {EntityService} from '../../services/entity-service';
import {StringUtils} from '../../utils/string-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-customer-furnished-parts-required',
    templateUrl: './customer-furnished-parts-required.component.html',
    styleUrls: ['./customer-furnished-parts-required.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatProgressSpinner, DatePipe]
})
export class CustomerFurnishedPartsRequiredComponent implements OnChanges, OnInit {
  private readonly iecTaskService = inject(IecTaskService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly taskResponseService = inject(TaskResponseService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  readonly taskIdParam = input.required<string>({ alias: 'taskId' });


  readonly task = signal<IecTask>(undefined!);
  response!: TaskResponse;
  readonly isBusy = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  readonly assignedTo = signal<string>(undefined!);
  isAccepted!: boolean;
  private readonly taskId = signal<number>(undefined!);
  private readonly personLogin = signal<PersonLogin>(undefined!);

  private loadTaskDetails(): void {
    this.isBusy.set(true);
    this.iecTaskService.getTask(this.taskId()).subscribe(result => {
      this.isBusy.set(false);
      this.task.set(result);
      if (this.task()) {
        if (this.task().endTime) {
          this.errorMessage.set('This task has been closed, you can close this window or use the links above to go to a different screen');
          this.task.set(undefined!);
          this.personLogin.set(undefined!);
          this.entity.set(undefined!);
        } else {
          this.iecTaskService.getAssignedTo(this.task().assignee).subscribe(description => this.assignedTo.set(description));
          if (this.task().entityId) {
            const entityPattern = new RegExp(/^((QT|MR|SR|R)\d{8})$/);
            if (entityPattern.test(this.task().entityId)) {
              this.isBusy.set(true);
              const entityName: string = this.task().entityId.startsWith('QT') ? 'QUOTE' : 'CONTRACT';
              this.entityService.getEntity(entityName, this.task().entityId, this.task().entityRevision).subscribe(entity => {
                this.entity.set(entity);
                this.isBusy.set(false);
              }, error => this.handleError(error));
            }
          }
        }
      } else {
        this.errorMessage.set('No task was found with ID ' + this.taskId());
      }
    }, error => this.handleError(error));
  }

  canCloseTask(): boolean {
    if (!this.task()) {
      return false;
    }
    if (!this.personLogin()) {
      return false;
    }
    return this.isCloseAccessAllowed();
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin.set(this.personLoginService.getCachedLogin()!);
      this.initialize();
    }
  }

  closeTask(isApproved: boolean): void {
    if (this.task()) {
      if (!this.isCloseAccessAllowed()) {
        this.errorMessage.set('You do not have permission to close this task');
      } else {
        this.isBusy.set(true);
        this.response = new TaskResponse();
        this.response.taskId = this.taskId();
        this.response.responder = this.personLogin();
        this.response.approved = isApproved;
        this.taskResponseService.closeTask(this.response).subscribe(() => {
          this.isBusy.set(false);
          this.loadTaskDetails();
        }, error => this.handleError(error));
      }
    }
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private isCloseAccessAllowed() {
    if (!this.task()) {
      return true;
    }
    if (this.personLoginService.hasRole(RoleName.IT)) {
      return true;
    }
    return this.personLogin().token === this.task().assignee
      || this.personLogin().groups.filter(group => group.token === this.task().assignee).length > 0;
  }



  private initialize(): void {
    const id: string = this.taskIdParam();
    if (id && !isNaN(+id)) {
      this.taskId.set(+id);
      this.loadTaskDetails();
    } else {
      this.errorMessage.set('Cannot find task information with an ID of ' + id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['taskIdParam'].firstChange) {
      this.initialize();
    }
  }
}
